import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { MSym } from '@storeo/ui';
import { useAuth } from '../../lib/auth';

type NavItem = { to: string; label: string; icon: string; end?: boolean; soon?: boolean };
type NavGroup = { label: string; items: NavItem[] };

const NAV: NavGroup[] = [
  {
    label: 'Visão Executiva',
    items: [
      { to: '/', label: 'Cockpit 360°', icon: 'space_dashboard', end: true },
      { to: '/metricas', label: 'Métricas & BI', icon: 'query_stats' },
    ],
  },
  {
    label: 'Gestão de Rede',
    items: [
      { to: '/parceiros', label: 'Lojas & Subdomínios', icon: 'storefront' },
      { to: '/ranking', label: 'Ranking & Leaderboard', icon: 'leaderboard' },
    ],
  },
  {
    label: 'Comunidade',
    items: [
      { to: '/clientes', label: 'Clientes & Alunos Global', icon: 'groups' },
      { to: '/tickets', label: 'Central de Tickets', icon: 'support_agent' },
    ],
  },
  {
    label: 'Infra & Engine',
    items: [
      { to: '/bots', label: 'Bots & Automação', icon: 'smart_toy' },
      { to: '/saude', label: 'Auditoria & Logs', icon: 'verified_user' },
      { to: '/usuarios', label: 'Usuários & Acessos', icon: 'admin_panel_settings' },
    ],
  },
];

/** Shell fixo (rail + topbar) — redesign Lumina do admin interno ("Super Admin HQ" no
 *  protótipo). Nav completa do protótipo (9 telas); só as 3 com API real (Cockpit, Lojas,
 *  Auditoria) navegam — o resto fica "em breve" até a Fase 2. */
export function Shell() {
  const { claims, logout } = useAuth();
  const { pathname } = useLocation();
  const person = claims?.name ?? claims?.email ?? 'Equipe';
  const initial = person.charAt(0).toUpperCase();
  const crumb = NAV.flatMap((g) => g.items).find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)))?.label ?? 'Storeo Admin';

  return (
    <div className="lumina bg-surface text-on-surface font-body-md text-body-md min-h-screen">
      <aside className="fixed left-0 top-0 h-full w-64 bg-surface-container-lowest/95 backdrop-blur-2xl z-50 flex flex-col justify-between shadow-[0_1px_8px_rgba(0,0,0,0.3)]">
        <div className="flex flex-col overflow-y-auto">
          <div className="h-16 px-space-md flex items-center gap-space-xs shrink-0">
            <div className="w-9 h-9 rounded-lg bg-surface-container-high flex items-center justify-center text-primary shrink-0">
              <MSym name="terminal" className="text-[20px]" />
            </div>
            <span className="font-headline-sm text-headline-sm text-on-surface tracking-tight font-bold leading-none">Storeo Admin</span>
          </div>
          <nav className="flex flex-col gap-space-md px-space-sm py-space-xs">
            {NAV.map((group) => (
              <div key={group.label} className="flex flex-col gap-space-3xs">
                <span className="px-space-xs font-label-sm text-label-sm text-outline uppercase tracking-wider">{group.label}</span>
                {group.items.map((item) =>
                  item.soon ? (
                    <span
                      key={item.to}
                      className="flex items-center gap-space-sm px-space-sm py-space-xs rounded-lg text-outline/60 cursor-default"
                      title="Em breve"
                    >
                      <MSym name={item.icon} className="text-[20px]" />
                      <span className="font-label-lg text-label-lg flex-1">{item.label}</span>
                      <span className="font-label-sm text-label-sm px-space-2xs rounded bg-surface-container-high">em breve</span>
                    </span>
                  ) : (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `flex items-center gap-space-sm px-space-sm py-space-xs rounded-lg transition-colors font-label-lg text-label-lg ${
                          isActive
                            ? 'bg-primary-container text-on-primary-container font-semibold'
                            : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'
                        }`
                      }
                    >
                      <MSym name={item.icon} className="text-[20px]" />
                      <span>{item.label}</span>
                    </NavLink>
                  ),
                )}
              </div>
            ))}
          </nav>
        </div>
        <div className="p-space-sm shrink-0">
          <div className="p-space-sm rounded-xl bg-surface-container-low flex items-center justify-between gap-space-xs">
            <div className="flex items-center gap-space-xs min-w-0">
              <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-on-primary font-bold shrink-0">{initial}</span>
              <div className="flex flex-col min-w-0">
                <span className="font-label-md text-label-md text-on-surface font-bold truncate">{person}</span>
                <span className="font-label-sm text-label-sm text-secondary truncate">Equipe Storeo</span>
              </div>
            </div>
            <button onClick={logout} aria-label="Sair" title="Sair" className="text-outline hover:text-on-surface shrink-0">
              <MSym name="logout" className="text-[20px]" />
            </button>
          </div>
        </div>
      </aside>

      <div className="pl-64">
        <header className="fixed top-0 left-64 right-0 h-16 bg-surface/80 backdrop-blur-xl z-40 flex items-center justify-between px-space-xl">
          <span className="font-headline-sm text-headline-sm text-on-surface font-bold">{crumb}</span>
          <div className="flex items-center gap-space-md">
            <button className="w-9 h-9 rounded-lg bg-surface-container flex items-center justify-center text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface transition-all" type="button">
              <MSym name="search" className="text-[18px]" />
            </button>
            <button className="w-9 h-9 rounded-lg bg-surface-container flex items-center justify-center text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface transition-all" type="button">
              <MSym name="notifications" className="text-[18px]" />
            </button>
          </div>
        </header>

        <main className="w-full pt-16 bg-surface px-space-xl min-h-screen">
          <div className="flex flex-col w-full pb-space-3xl gap-space-xl pt-space-lg">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
